import * as React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import type { VerificationDocType } from '@cp3/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { useToast } from './ui/toast';
import { useUser } from '../auth/AuthProvider';
import {
  useDeleteVerificationDoc,
  useDownloadUrl,
  useMyVerificationDocs,
  useSetMyProfile,
  useUploadVerificationDoc,
  type VerificationDocRow,
} from '../hooks/useMyProfile';
import { formatCallableError } from '../lib/callableError';
import { cn } from '../lib/utils';

const DOC_LABELS: Record<VerificationDocType, string> = {
  passport: 'Reisepass / Personalausweis',
  companyCert: 'Inhaberschafts-Zertifikat',
  utilityBill: 'Telefon- oder Stromrechnung',
};

const DOC_HINTS: Record<VerificationDocType, string> = {
  passport: 'PDF oder Foto, klar lesbar.',
  companyCert: 'Handelsregisterauszug, Gewerbeanmeldung o. ä.',
  utilityBill: 'Adressnachweis, nicht älter als 90 Tage.',
};

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';

export function ProfileDialog({ onClose }: { onClose: () => void }) {
  const { user, profile } = useUser();
  const { toast } = useToast();
  const setProfile = useSetMyProfile();

  const [firstName, setFirstName] = React.useState(profile?.firstName ?? '');
  const [lastName, setLastName] = React.useState(profile?.lastName ?? '');
  const [displayName, setDisplayName] = React.useState(profile?.displayName ?? '');

  // Auto-suggest displayName when first/last change and user hasn't typed
  // a custom one. Mirrors what most identity providers do.
  const initialDisplayRef = React.useRef(profile?.displayName ?? '');
  React.useEffect(() => {
    const combined = `${firstName} ${lastName}`.trim();
    if (combined && (!displayName || displayName === initialDisplayRef.current)) {
      setDisplayName(combined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName]);

  const dirty =
    firstName !== (profile?.firstName ?? '') ||
    lastName !== (profile?.lastName ?? '') ||
    displayName !== (profile?.displayName ?? '');

  async function onSaveProfile() {
    try {
      await setProfile.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayName.trim(),
      });
      initialDisplayRef.current = displayName.trim();
      toast({ title: 'Profil aktualisiert' });
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: formatCallableError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Mein Profil</DialogTitle>
          <DialogDescription>
            Persönliche Daten und Verifikations-Dokumente. Die Dokumente liegen
            verschlüsselt in deinem persönlichen Storage-Bereich; nur du und ein
            Plattform-Admin können sie sehen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Persönliche Daten
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="profile-fn">Vorname</Label>
                <Input
                  id="profile-fn"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={80}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="profile-ln">Nachname</Label>
                <Input
                  id="profile-ln"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={80}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-dn">Anzeigename</Label>
              <Input
                id="profile-dn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Wie du in Sidebar und Begrüßung erscheinst"
                maxLength={80}
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                E-Mail: <span className="font-mono">{user?.email}</span>
              </p>
              <Button onClick={() => void onSaveProfile()} disabled={!dirty || setProfile.isPending}>
                {setProfile.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Speichern
              </Button>
            </div>
          </section>

          <VerificationSection />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Verification section ────────────────────────────────────────────────

function VerificationSection() {
  const { items, loading } = useMyVerificationDocs();
  const map = React.useMemo(() => {
    const m: Partial<Record<VerificationDocType, VerificationDocRow>> = {};
    for (const it of items) m[it.type] = it;
    return m;
  }, [items]);

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Verifikations-Dokumente
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Maximal 10 MB pro Datei. PDF, JPG, PNG oder WEBP. Wird verschlüsselt
          in Firebase Storage abgelegt — kein Cloud-Sharing, keine Drittanbieter.
        </p>
      </div>
      {loading ? (
        <div className="flex h-16 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-3.5 animate-spin" /> Lade …
        </div>
      ) : (
        <div className="space-y-3">
          <DocSlot type="passport" doc={map.passport ?? null} />
          <DocSlot type="companyCert" doc={map.companyCert ?? null} />
          <DocSlot type="utilityBill" doc={map.utilityBill ?? null} requiresDate />
        </div>
      )}
    </section>
  );
}

const NINETY_DAYS_MS = 90 * 86_400_000;

function DocSlot({
  type,
  doc,
  requiresDate,
}: {
  type: VerificationDocType;
  doc: VerificationDocRow | null;
  requiresDate?: boolean;
}) {
  const upload = useUploadVerificationDoc();
  const remove = useDeleteVerificationDoc();
  const url = useDownloadUrl(doc?.storagePath ?? null);
  const { toast } = useToast();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [pendingDate, setPendingDate] = React.useState<string>('');

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (requiresDate && !pendingDate) {
      toast({
        title: 'Bitte Rechnungsdatum eintragen',
        description: 'Wir prüfen, dass die Rechnung nicht älter als 90 Tage ist.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await upload.mutateAsync({
        type,
        file,
        documentDate: pendingDate ? new Date(pendingDate) : null,
      });
      setPendingDate('');
      toast({ title: `${DOC_LABELS[type]} hochgeladen` });
    } catch (err) {
      toast({
        title: 'Upload fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function onDelete() {
    if (!confirm(`${DOC_LABELS[type]} wirklich löschen?`)) return;
    try {
      await remove.mutateAsync(type);
      toast({ title: 'Gelöscht' });
    } catch (err) {
      toast({
        title: 'Löschen fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const tooOld =
    requiresDate &&
    doc?.documentDate &&
    Date.now() - doc.documentDate.getTime() > NINETY_DAYS_MS;

  return (
    <Card className={cn(tooOld && 'border-amber-500/40 bg-amber-500/5')}>
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <FileText className="size-4 text-muted-foreground" />
          <span className="font-medium">{DOC_LABELS[type]}</span>
          {doc ? (
            <Badge variant="success">hochgeladen</Badge>
          ) : (
            <Badge variant="muted">offen</Badge>
          )}
          {tooOld && (
            <Badge variant="warning">
              <AlertTriangle className="mr-1 size-3" /> &gt; 90 Tage
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{DOC_HINTS[type]}</p>

        {doc && (
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">{doc.fileName}</span> ·{' '}
            {formatBytes(doc.sizeBytes)}
            {doc.uploadedAt && (
              <>
                {' '}· hochgeladen {doc.uploadedAt.toLocaleDateString('de-DE')}
              </>
            )}
            {requiresDate && doc.documentDate && (
              <>
                {' '}· Rechnung vom {doc.documentDate.toLocaleDateString('de-DE')}
              </>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {requiresDate && (
            <div className="space-y-0.5">
              <Label htmlFor={`date-${type}`} className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Rechnungsdatum
              </Label>
              <Input
                id={`date-${type}`}
                type="date"
                value={pendingDate}
                onChange={(e) => setPendingDate(e.target.value)}
                className="h-9 w-40"
              />
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => void onPick(e)}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : (
              <Upload className="mr-1 size-3.5" />
            )}
            {doc ? 'Ersetzen' : 'Hochladen'}
          </Button>
          {doc && url.data && (
            <Button asChild type="button" size="sm" variant="ghost">
              <a href={url.data} target="_blank" rel="noreferrer">
                Ansehen <ExternalLink className="ml-1 size-3.5" />
              </a>
            </Button>
          )}
          {doc && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => void onDelete()}
              disabled={remove.isPending}
            >
              <Trash2 className="mr-1 size-3.5" /> Löschen
            </Button>
          )}
          {!doc && type === 'passport' && (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <CheckCircle2 className="size-3" /> sichtbar nur für dich + Plattform-Admin
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
